// @ts-check

/** localStorage key holding the current admin dashboard session. */
export const ADMIN_SESSION_STORAGE_KEY = 'puzzle_admin_session';

/**
 * @typedef {Object} AdminSession
 * @property {string} token
 * @property {number} expiresAt — Epoch ms
 */

/**
 * Reads the stored session, discarding anything malformed or expired.
 *
 * @param {{ getItem: (k: string) => string|null, removeItem: (k: string) => void }} store
 * @param {number} [now]
 * @returns {AdminSession|null}
 */
export function readAdminSession(store, now = Date.now()) {
  if (!store) return null;
  let raw = null;
  try {
    raw = store.getItem(ADMIN_SESSION_STORAGE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    const token = String(parsed?.token || '').trim();
    const expiresAt = Number(parsed?.expiresAt);
    if (!token || !Number.isFinite(expiresAt) || expiresAt <= now) {
      store.removeItem(ADMIN_SESSION_STORAGE_KEY);
      return null;
    }
    return { token, expiresAt };
  } catch {
    store.removeItem(ADMIN_SESSION_STORAGE_KEY);
    return null;
  }
}

/**
 * Persists a freshly minted session.
 *
 * @param {{ setItem: (k: string, v: string) => void }} store
 * @param {AdminSession} session
 * @returns {void}
 */
export function writeAdminSession(store, session) {
  if (!store || !session?.token) return;
  try {
    store.setItem(ADMIN_SESSION_STORAGE_KEY, JSON.stringify({
      token: session.token,
      expiresAt: Number(session.expiresAt) || 0
    }));
  } catch {
    // A storage quota or private-mode failure only costs the operator a re-login.
  }
}

/**
 * Turns a login response into a session, rejecting anything unusable.
 *
 * @param {any} payload
 * @param {number} [now]
 * @returns {AdminSession|null}
 */
export function sessionFromLoginResponse(payload, now = Date.now()) {
  const token = String(payload?.token || '').trim();
  const expiresAt = Number(payload?.expiresAt);
  if (!token) return null;
  return {
    token,
    // A server without a usable expiry is treated as a short session rather than
    // a permanent one, so a bad clock cannot mint an immortal login.
    expiresAt: Number.isFinite(expiresAt) && expiresAt > now ? expiresAt : now + 60 * 60 * 1000
  };
}
